import { useCallback, useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Underline from '@tiptap/extension-underline';
import Superscript from '@tiptap/extension-superscript';
import Subscript from '@tiptap/extension-subscript';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import {
  TableCellEx,
  TableHeaderEx,
  TableTrailingParagraph,
} from './TableCellExtension';
import Collaboration from '@tiptap/extension-collaboration';
import CollaborationCursor from '@tiptap/extension-collaboration-cursor';
import * as Y from 'yjs';
import {
  Bold,
  Italic,
  Strikethrough,
  Underline as UnderlineIcon,
  Superscript as SuperscriptIcon,
  Subscript as SubscriptIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Heading1,
  Heading2,
  List,
  ListOrdered,
  Code,
  Quote,
  KeyRound,
  History,
  WifiOff,
  Lock as LockIcon,
  Pencil,
  Check,
  RefreshCw,
  Palette,
  Highlighter,
  Table as TableIcon,
} from 'lucide-react';
import { useVault } from '../../../core/lib/vault';
import {
  getDecryptedDocument,
  listDecryptedDocuments,
  subscribe,
  type DecryptedDocument,
} from '../sync';
import { localStore } from '../db';
import { useOnline } from '../lib/useOnline';
import { useCanEdit as useVaultCanEdit } from '../lib/usePermissions';
import { useSyncStatus } from '../lib/useSyncStatus';
import {
  acquireDoc,
  releaseDoc,
  schedulePersist,
  flushDoc,
  wireOutbound,
  onRemoteApplied,
  requestYjsSync,
  isSnapshotInFlight,
  getAwareness,
  wireAwarenessOutbound,
  applyServerSnapshot,
} from '../lib/yjsCollab';
import { colorForUser } from '../lib/userColor';
import { sendWs, subscribeChanges } from '../sync/ws';
import { fromB64 } from '../../../core/crypto';
import { PasswordField, type PasswordFieldAction } from './PasswordFieldExtension';
import {
  InlineImage,
  INLINE_IMAGE_REPLACE_EVENT,
  type InlineImageReplaceDetail,
} from './InlineImageExtension';
import { AttachmentChip } from './AttachmentChipExtension';
import { ColorPopover } from './ColorPopover';
import { TableMenu } from './TableMenu';
import { TableBubbleMenu } from './TableBubbleMenu';
import { TableChrome } from './TableChrome';
import {
  scheduleDerivedMarkdown,
  flushDerivedMarkdown,
} from '../lib/derivedMarkdown';
import { RevealPasswordModal } from './RevealPasswordModal';
import { PasswordEditorModal } from './PasswordEditorModal';
import { TabBar } from './TabBar';
import { VersionHistoryDrawer } from './VersionHistoryDrawer';
import { PresenceBar } from './PresenceBar';
import { openAttachmentPreview } from '../lib/attachmentPreview';
import {
  ATTACHMENT_DRAG_MIME,
  DOC_UPDATED_EVENT,
  readAttachmentDrag,
  type AttachmentDragPayload,
} from '../lib/attachmentTypes';
import { dragHasFiles, uploadFile, uploadFiles } from '../lib/attachmentUpload';

export function EditorPane({
  pageId,
  activeTabId,
  onTabSelect,
  initialSyncComplete,
}: {
  pageId: string | null;
  activeTabId: string | null;
  onTabSelect: (tabId: string | null) => void;
  /**
   * Latched true once the boot `pullAll + flushOutbox` has resolved. While
   * false, we render a skeleton and skip `acquireDoc` — IDB may still be
   * carrying the pre-pull state from a prior session, and seeding a Y.Doc
   * from it would surface stale content even after the pull lands. See
   * plan F1.
   */
  initialSyncComplete: boolean;
}) {
  const vaultKey = useVault((s) => s.vaultKey);
  const currentUser = useVault((s) => s.user);
  const online = useOnline();
  // Viewers (role='viewer') must never land in editable mode, regardless of
  // the `editingEnabled` toggle state. Gating here collapses the permission
  // check into the existing `canEdit` computation downstream, so the title
  // field, the formatting toolbar, the Edit/Done button and TipTap's
  // `editable` flag all pick up the restriction from one place.
  const permittedToEdit = useVaultCanEdit();
  const syncStatus = useSyncStatus();
  // Active doc has work in flight (mid-retry on the snapshot loop)? If so,
  // the Done button must say "Saving…" instead of "Done" so users don't walk
  // away thinking edits are durable when they're still mid-merge.
  const docIsRetrying = activeTabId ? syncStatus.retryingDocCount > 0 : syncStatus.retryingDocCount > 0;
  const [doc, setDoc] = useState<Awaited<ReturnType<typeof getDecryptedDocument>>>(null);
  const [page, setPage] = useState<DecryptedDocument | null>(null);
  const [tabs, setTabs] = useState<DecryptedDocument[]>([]);
  const [title, setTitle] = useState('');
  const activeDocId = activeTabId ?? pageId;
  const [reveal, setReveal] = useState<{ id: string; label: string; value: string } | null>(null);
  const [pwEdit, setPwEdit] = useState<
    | { mode: 'create' }
    | { mode: 'edit'; id: string; label: string; value: string }
    | null
  >(null);
  const [pwError, setPwError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [colorPopover, setColorPopover] = useState<'color' | 'highlight' | null>(null);
  const [tableMenuOpen, setTableMenuOpen] = useState(false);
  const colorAnchorRef = useRef<HTMLDivElement | null>(null);
  const highlightAnchorRef = useRef<HTMLDivElement | null>(null);
  const tableAnchorRef = useRef<HTMLDivElement | null>(null);

  // Stabilise popover close callbacks. Without `useCallback`, each
  // EditorPane render produces a fresh arrow function, which is the
  // dependency of each popover's click-outside `useEffect`. The listener
  // would unbind+rebind on every keystroke. With these stable refs the
  // listener stays bound for the popover's lifetime.
  const closeColorPopover = useCallback(() => setColorPopover(null), []);
  const closeTableMenu = useCallback(() => setTableMenuOpen(false), []);
  /** View/edit mode. Defaults to view-only and resets on every document change so
   * edit mode never leaks across pages and techs don't accidentally type into
   * a page they meant to just read. */
  const [editingEnabled, setEditingEnabled] = useState(false);
  const [dropOver, setDropOver] = useState(false);
  const [dropBusy, setDropBusy] = useState(false);
  const [dropError, setDropError] = useState<string | null>(null);
  /** The Y.Doc state for the currently-active document. Replaced on docId change. */
  const docStateRef = useRef<ReturnType<typeof acquireDoc> | null>(null);
  /**
   * Reference to the EditorPane's root `<section>`. Used by the
   * click-outside auto-Done listener below — any mousedown whose target
   * is NOT inside this element (i.e. on the sidebar, the app header,
   * the app launcher, etc.) implicitly flushes + exits edit mode. This
   * means users who forget to click Done before switching context still
   * get their edits persisted promptly AND don't leave the editor in
   * write-mode where a stray keystroke could modify the page.
   */
  const rootRef = useRef<HTMLElement | null>(null);

  // Acquire / release the Y.Doc for the active doc. Outbound WS wiring is
  // handled in a separate effect below, keyed on canEdit, so viewers in
  // read-only mode never broadcast or snapshot.
  useEffect(() => {
    docStateRef.current = null;
    setDoc(null);
    setTitle('');
    setEditingEnabled(false); // reset to view-only on every doc switch
    if (!activeDocId || !vaultKey) return;
    // Wait until the boot pull has refreshed IDB before seeding a Y.Doc.
    // Without this, an unlock after long idle can seed from pre-pull
    // (stale) ciphertext and the user sees yesterday's content. The effect
    // re-runs once `initialSyncComplete` flips true. See plan F1.
    if (!initialSyncComplete) return;

    let released = false;
    let acquiredId: string | null = null;

    (async () => {
      const d = await getDecryptedDocument(activeDocId, vaultKey);
      if (released) return;
      setDoc(d);

      // The row version lives on the raw DTO, not on DecryptedDocument.
      // Read it straight from the local store so the optimistic lock has
      // a baseline on first open.
      let initialVersion: number | undefined;
      try {
        const row = await localStore.getDocument(activeDocId);
        initialVersion = row?.data?.payload?.version;
      } catch {
        /* ignore — lock just degrades to last-writer-wins */
      }

      const state = acquireDoc(
        activeDocId,
        d?.customerId ?? '',
        d?.parentId ?? null,
        d
          ? {
              title: d.title,
              markdown: d.markdown,
              tags: d.tags,
              icon: d.icon,
              color: d.color,
              yjsState: d.yjsState,
            }
          : null,
        vaultKey,
        initialVersion,
      );
      if (released) {
        releaseDoc(activeDocId);
        return;
      }
      acquiredId = activeDocId;
      docStateRef.current = state;

      // Late-joiner handshake. Server has no replay buffer for live yjs_update
      // frames; without this, opening a doc that another peer is editing
      // would seed an empty Y.Doc and incoming deltas would be held as
      // "pending" forever (referencing ops we never received). Peers with the
      // doc loaded will respond with the missing diff via yjs_sync_response.
      requestYjsSync(activeDocId, sendWs);

      setRefreshNonce((n) => n + 1);
    })();

    return () => {
      released = true;
      // Flush any pending markdown derivation for this doc BEFORE we
      // release the Y.Doc, so the freshest HTML lands in `state.derivedMarkdown`
      // and gets included in the next snapshot. The flush captures HTML at
      // schedule time (string, not closure), so it stays correct even after
      // the editor is destroyed.
      if (acquiredId) flushDerivedMarkdown(acquiredId);
      if (acquiredId) releaseDoc(acquiredId);
      docStateRef.current = null;
    };
  }, [activeDocId, vaultKey, initialSyncComplete]);

  const editor = useEditor(
    {
      extensions: [
        // Collaboration supplies its own history; starter-kit's must be off.
        StarterKit.configure({ heading: { levels: [1, 2, 3] }, history: false }),
        Placeholder.configure({ placeholder: 'Start writing…' }),
        Link.configure({ openOnClick: false }),
        TaskList,
        TaskItem.configure({ nested: true }),
        Underline,
        // TextStyle must precede Color — Color is a TextStyle-dependent mark.
        TextStyle,
        Color,
        Highlight.configure({ multicolor: true }),
        Superscript,
        Subscript,
        TextAlign.configure({ types: ['heading', 'paragraph'] }),
        Table.configure({ resizable: true, HTMLAttributes: { class: 'tt-table' } }),
        TableRow,
        TableHeaderEx,
        TableCellEx,
        TableTrailingParagraph,
        PasswordField,
        InlineImage,
        AttachmentChip,
        ...(docStateRef.current
          ? [
              Collaboration.configure({ document: docStateRef.current.ydoc }),
              // Coloured remote cursors + selections. Reads from the doc's
              // y-protocols Awareness instance — same one our outbound WS
              // wiring broadcasts to peers below. The user object is what
              // peers see in PresenceBar; cursor decorations are rendered
              // inline by this extension via PM decorations.
              CollaborationCursor.configure({
                provider: { awareness: docStateRef.current.awareness },
                user: currentUser
                  ? {
                      name: currentUser.username,
                      color: colorForUser(currentUser.id),
                    }
                  : { name: 'Anonymous', color: '#999999' },
              }),
            ]
          : []),
      ],
      editable: online && editingEnabled && permittedToEdit,
      editorProps: {
        attributes: { class: 'tiptap prose-invert max-w-none focus:outline-none' },
      },
      onUpdate: ({ editor: ed }) => {
        if (!activeDocId) return;
        // Coalesce the markdown derivation. Turndown over the whole HTML
        // tree on every PM transaction was the dominant cost during typing
        // bursts (and on remote yjs apply). The 1 s trailing debounce never
        // misses a snapshot — `persistSnapshot`'s 3 s / 30 s schedule still
        // sees the freshest markdown. Doc switches and the export flow flush
        // the pending derivation.
        scheduleDerivedMarkdown(activeDocId, ed.getHTML());
      },
    },
    [activeDocId, refreshNonce],
  );

  const canEdit = online && editingEnabled && permittedToEdit;

  // Wire outbound Yjs broadcasts + snapshots ONLY while in edit mode. Viewers
  // in read-only mode stay bound to the Y.Doc (so they still receive remote
  // updates) but never broadcast or schedule snapshots.
  useEffect(() => {
    if (!activeDocId || !vaultKey || !canEdit) return;
    const state = docStateRef.current;
    if (!state) return;
    const teardown = wireOutbound(
      activeDocId,
      vaultKey,
      sendWs,
      () => schedulePersist(activeDocId),
    );
    return () => {
      teardown();
    };
  }, [activeDocId, vaultKey, canEdit, refreshNonce]);

  // Wire awareness outbound INDEPENDENTLY of canEdit — viewers should also
  // appear in the presence list and (on a future hover) be visible to peers.
  // Sets the local user identity so peers see "Joe Bloggs" + a colour.
  // Cleared in releaseDocAsync via removeAwarenessStates so disconnects are
  // visible promptly to other clients.
  useEffect(() => {
    if (!activeDocId || !vaultKey || !currentUser) return;
    const state = docStateRef.current;
    if (!state) return;
    const teardown = wireAwarenessOutbound(activeDocId, vaultKey, sendWs);
    state.awareness.setLocalStateField('user', {
      id: currentUser.id,
      name: currentUser.username,
      color: colorForUser(currentUser.id),
    });
    return () => {
      teardown();
    };
  }, [activeDocId, vaultKey, currentUser, refreshNonce]);

  // Subscribe to the Y.Doc's meta map so the title input updates when other
  // users (or our own other tab) rename the page.
  useEffect(() => {
    const state = docStateRef.current;
    if (!state) {
      setTitle('');
      return;
    }
    const read = () => setTitle((state.meta.get('title') as string | undefined) ?? '');
    read();
    const observer = () => read();
    state.meta.observe(observer);
    return () => state.meta.unobserve(observer);
  }, [refreshNonce]);

  // Auto-Done on click-outside. When the user clicks anywhere outside the
  // EditorPane root (sidebar, header, app launcher, a different pane), if
  // edit mode is active, kick a best-effort flush and drop back to
  // read-only. Implicit "Done" — the user never has to remember the button.
  // We do NOT cancel the outside click (no preventDefault / stopPropagation)
  // so the sidebar action they intended still runs.
  useEffect(() => {
    if (!editingEnabled) return;
    function onMouseDown(e: MouseEvent) {
      const target = e.target as Node | null;
      if (!target) return;
      const root = rootRef.current;
      if (root && root.contains(target)) return;
      // Outside — implicit Done.
      if (activeDocId) {
        void flushDoc(activeDocId).catch((err) => {
          console.warn('vault: flush failed (outside-click)', err);
        });
      }
      setEditingEnabled(false);
    }
    // `capture: true` so we fire before React's synthetic-event handlers —
    // this way a sidebar click flushes the editor state before that click's
    // own handler runs its own flushDoc / navigation state changes.
    window.addEventListener('mousedown', onMouseDown, { capture: true });
    return () => window.removeEventListener('mousedown', onMouseDown, { capture: true });
  }, [editingEnabled, activeDocId]);

  // Handle reveal/edit events dispatched by the PasswordField NodeView.
  useEffect(() => {
    function onAction(e: Event) {
      const d = (e as CustomEvent<PasswordFieldAction>).detail;
      if (!d) return;
      if (d.kind === 'reveal') {
        setReveal({ id: d.id, label: d.label, value: d.value });
      } else {
        setPwEdit({ mode: 'edit', id: d.id, label: d.label, value: d.value });
      }
    }
    window.addEventListener('dragonvault:password-field', onAction as EventListener);
    return () => window.removeEventListener('dragonvault:password-field', onAction as EventListener);
  }, []);

  // Handle replace-image requests from the InlineImage NodeView. The NodeView
  // doesn't know which customer the document belongs to (and shouldn't —
  // mirrors the password-field event pattern), so it hands the picked File
  // up here for upload + commit.
  useEffect(() => {
    async function onReplace(e: Event) {
      const detail = (e as CustomEvent<InlineImageReplaceDetail>).detail;
      if (!detail || !editor || !doc || !vaultKey) return;
      try {
        const payload = await uploadFile(detail.file, doc.customerId, vaultKey);
        (editor.commands as any).setInlineImageAttachmentId({
          id: detail.id,
          attachmentId: payload.id,
        });
      } catch (err) {
        console.warn('vault: inline-image replace failed', err);
        setDropError((err as any)?.body?.error ?? (err as any)?.message ?? 'Replace failed.');
        setTimeout(() => setDropError(null), 4000);
      }
    }
    window.addEventListener(INLINE_IMAGE_REPLACE_EVENT, onReplace as EventListener);
    return () => window.removeEventListener(INLINE_IMAGE_REPLACE_EVENT, onReplace as EventListener);
  }, [editor, doc, vaultKey]);

  // Toggle edit-ability based on the view/edit switch, connectivity, AND
  // the caller's vault role (viewers are pinned to read-only even if they
  // somehow trip the toggle).
  useEffect(() => {
    editor?.setEditable(online && editingEnabled && permittedToEdit);
  }, [online, editingEnabled, permittedToEdit, editor]);

  // Force a PM view flush whenever a remote Yjs update lands. In view-only
  // mode (editable=false), we observed that y-prosemirror applies the Y.Doc
  // changes but the DOM doesn't always reflect them until the component
  // remounts. Dispatching a no-op transaction via updateState re-runs PM's
  // view decoration so remote edits render live for viewers too.
  useEffect(() => {
    if (!editor || !activeDocId) return;
    return onRemoteApplied(activeDocId, () => {
      try {
        const view = (editor as any).view;
        if (view) view.updateState(view.state);
      } catch {
        /* editor destroyed mid-update */
      }
    });
  }, [editor, activeDocId, refreshNonce]);

  // On reconnect: refetch the doc and merge any newer yjsState from the server
  // into our local Y.Doc. Yjs updates are idempotent, so re-applying a state
  // we already have is a no-op. Uses `applyServerSnapshot` (NOT raw
  // `Y.applyUpdate`) so the `onRemoteApplied` listener chain fires and TipTap's
  // view repaints — without that, read-only viewers see the doc state update
  // in memory but not in the DOM.
  useEffect(() => {
    if (!online || !activeDocId || !vaultKey) return;
    let cancelled = false;
    (async () => {
      const d = await getDecryptedDocument(activeDocId, vaultKey);
      if (cancelled) return;
      if (d?.yjsState) applyServerSnapshot(activeDocId, d.yjsState);
    })();
    return () => {
      cancelled = true;
    };
  }, [online, activeDocId, vaultKey]);

  // Re-merge on remote snapshot. When a peer's persistSnapshot lands, the
  // server emits a `change_event` over WS — `sync/ws.ts` debounces a
  // `pullAll()` 250 ms later, then `notify()` fires. We listen on the
  // post-pullAll notify (rather than the raw change_event) so that when
  // we read IndexedDB it already reflects the peer's new yjsState.
  // `applyServerSnapshot` drives the `onRemoteApplied` listener chain so
  // TipTap's view repaints (read-only viewers otherwise see the Y.Doc
  // mutate in memory without a DOM repaint, and have to navigate away+back).
  // A `lastApplied` tracker keeps the work negligible on local mutations
  // that fire notify without changing the active doc.
  useEffect(() => {
    if (!activeDocId || !vaultKey) return;
    let cancelled = false;
    let lastApplied: string | undefined;
    const apply = () => {
      if (cancelled) return;
      if (isSnapshotInFlight(activeDocId)) return;
      void (async () => {
        try {
          const d = await getDecryptedDocument(activeDocId, vaultKey);
          if (cancelled) return;
          if (!d?.yjsState) return;
          if (d.yjsState === lastApplied) return;
          applyServerSnapshot(activeDocId, d.yjsState);
          lastApplied = d.yjsState;
        } catch (err) {
          console.warn('[yjs] notify re-merge failed', err);
        }
      })();
    };
    // Initial fire — covers the case where a doc was just acquired and
    // IndexedDB already holds a newer state than the seed we got.
    apply();
    const unsub = subscribe(apply);
    return () => {
      cancelled = true;
      unsub();
    };
  }, [activeDocId, vaultKey]);

  // Respond to external attachment drops onto the active doc: reload the
  // decrypted envelope so sidebar headers / attachment-derived state stays fresh.
  useEffect(() => {
    function onDocUpdated(e: Event) {
      const detail = (e as CustomEvent<{ docId: string }>).detail;
      if (!detail || detail.docId !== activeDocId) return;
      setRefreshNonce((n) => n + 1);
    }
    window.addEventListener(DOC_UPDATED_EVENT, onDocUpdated as EventListener);
    return () => window.removeEventListener(DOC_UPDATED_EVENT, onDocUpdated as EventListener);
  }, [activeDocId]);

  // Load the parent page metadata + its tab list (sub-docs).
  useEffect(() => {
    let cancelled = false;
    async function reload() {
      if (!pageId || !vaultKey) {
        setPage(null);
        setTabs([]);
        return;
      }
      const pg = await getDecryptedDocument(pageId, vaultKey);
      if (cancelled) return;
      setPage(pg as DecryptedDocument | null);
      const all = await listDecryptedDocuments(pg?.customerId ?? '', vaultKey);
      if (cancelled) return;
      const subDocs = all.filter((d) => d.parentId === pageId);
      setTabs(subDocs);
    }
    reload();
    const unsub = subscribe(reload);
    return () => {
      cancelled = true;
      unsub();
    };
  }, [pageId, vaultKey]);

  function updateTitle(next: string) {
    setTitle(next);
    const state = docStateRef.current;
    if (!state || !activeDocId) return;
    state.meta.set('title', next);
    schedulePersist(activeDocId);
  }

  function openPasswordInsert() {
    setPwEdit({ mode: 'create' });
  }

  function savePasswordField(label: string, value: string) {
    if (!editor || !pwEdit) return;
    if (pwEdit.mode === 'create') {
      (editor.commands as any).insertPasswordField({ label, value });
      setPwEdit(null);
      setPwError(null);
      return;
    }
    const ok = (editor.commands as any).updatePasswordField({ id: pwEdit.id, label, value });
    if (ok) {
      setPwEdit(null);
      setPwError(null);
    } else {
      // Field was deleted by a peer between when this user opened the modal
      // and when they hit Save. Keep the modal open so they can copy the
      // value they just typed before discarding — losing a freshly-typed
      // secret to an auto-dismissing toast was the previous failure mode.
      setPwError(
        'This password field was removed by another user. Your changes were not applied — copy your value below if you still need it, then close.',
      );
    }
  }

  // Cycle paragraph/heading text alignment: none → left → center → right → justify → none.
  // Single-button compromise so the toolbar still fits under the 768px wrap budget.
  function cycleTextAlign() {
    if (!editor) return;
    const isLeft = editor.isActive({ textAlign: 'left' });
    const isCenter = editor.isActive({ textAlign: 'center' });
    const isRight = editor.isActive({ textAlign: 'right' });
    const isJustify = editor.isActive({ textAlign: 'justify' });
    const chain = editor.chain().focus();
    if (!isLeft && !isCenter && !isRight && !isJustify) chain.setTextAlign('left').run();
    else if (isLeft) chain.setTextAlign('center').run();
    else if (isCenter) chain.setTextAlign('right').run();
    else if (isRight) chain.setTextAlign('justify').run();
    else chain.unsetTextAlign().run();
  }

  if (!pageId) {
    return (
      <section className="flex-1 bg-ink-950 flex items-center justify-center text-ink-500 text-sm">
        Select a page to view its contents.
      </section>
    );
  }

  // Initial-sync gate: we have a page selection (probably restored from
  // IDB) but the boot pull hasn't landed yet. Showing the editor here would
  // seed the Y.Doc from possibly-stale ciphertext — see plan F1.
  if (!initialSyncComplete) {
    return (
      <section className="flex-1 bg-ink-950 flex items-center justify-center text-ink-500 text-sm">
        <RefreshCw size={14} className="mr-2 animate-spin" />
        Syncing latest from server…
      </section>
    );
  }

  return (
    <section ref={rootRef} className="flex-1 flex flex-col min-w-0 bg-ink-950">
      <div className="flex items-center gap-2 border-b border-ink-800 px-4 py-2">
        <input
          className="flex-1 bg-transparent text-lg font-semibold focus:outline-none"
          value={title}
          onChange={(e) => updateTitle(e.target.value)}
          readOnly={!canEdit}
          placeholder={activeTabId ? 'Tab title' : 'Page title'}
        />
        <PresenceBar activeDocId={activeDocId} />
        {activeDocId && online && permittedToEdit && (
          <button
            onClick={() => {
              // Flush is fire-and-forget — do NOT await. releaseDocAsync
              // already keeps the Y.Doc alive across the flush window if
              // we later navigate, and the localStore mirror is already
              // current (500 ms debounce). Blocking the click here caused
              // the UI to appear frozen when the PUT was slow.
              if (editingEnabled && activeDocId) {
                void flushDoc(activeDocId).catch((err) => {
                  console.warn('vault: flush failed (done-button)', err);
                });
              }
              setEditingEnabled((v) => !v);
            }}
            className={`${editingEnabled ? 'btn-ghost' : 'btn-primary'} !px-3 !py-1 gap-1.5`}
            title={
              editingEnabled
                ? docIsRetrying
                  ? 'Edits still saving — keep this tab open until the indicator clears'
                  : 'Stop editing (switch to read-only)'
                : 'Enable editing'
            }
            aria-pressed={editingEnabled}
          >
            {editingEnabled
              ? docIsRetrying
                ? <RefreshCw size={14} className="animate-spin" />
                : <Check size={14} />
              : <Pencil size={14} />}
            <span className="text-xs font-medium">
              {editingEnabled ? (docIsRetrying ? 'Saving…' : 'Done') : 'Edit'}
            </span>
          </button>
        )}
        {activeDocId && (
          <button
            onClick={() => setShowHistory(true)}
            className="btn-ghost"
            title="Snapshots"
          >
            <History size={14} />
          </button>
        )}
      </div>

      {!online && (
        <div className="border-b border-amber-900 bg-amber-950/40 px-4 py-1.5 text-xs text-amber-300 flex items-center gap-2">
          <WifiOff size={12} /> Offline — editing paused. Changes will resume once you're back on the network.
        </div>
      )}

      {online && !editingEnabled && (
        <div className="border-b border-ink-800 bg-ink-900/40 px-4 py-1.5 text-xs text-ink-400 flex items-center gap-2">
          <LockIcon size={12} />
          {permittedToEdit
            ? `Read-only. Click the Edit button to edit this ${activeTabId ? 'tab' : 'page'}.`
            : 'Read-only — your vault role does not allow edits. Ask an admin to change your role.'}
        </div>
      )}

      {page && (
        <TabBar
          page={page}
          tabs={tabs}
          selectedTabId={activeTabId}
          editMode={canEdit}
          onSelect={(id) => {
            // Fire-and-forget flush — releaseDocAsync keeps the Y.Doc
            // alive until the PUT lands; blocking here made tab clicks
            // feel unresponsive on slow networks.
            if (activeDocId) {
              void flushDoc(activeDocId).catch((err) => {
                console.warn('vault: flush failed (tab-switch)', err);
              });
            }
            onTabSelect(id);
          }}
        />
      )}

      {canEdit && (
        <div className="flex items-center gap-1 border-b border-ink-800 px-2 py-1 text-ink-300 overflow-x-auto flex-nowrap md:flex-wrap">
          <ToolbarBtn onClick={() => editor?.chain().focus().toggleBold().run()} active={editor?.isActive('bold')}>
            <Bold size={14} />
          </ToolbarBtn>
          <ToolbarBtn onClick={() => editor?.chain().focus().toggleItalic().run()} active={editor?.isActive('italic')}>
            <Italic size={14} />
          </ToolbarBtn>
          <ToolbarBtn onClick={() => editor?.chain().focus().toggleStrike().run()} active={editor?.isActive('strike')}>
            <Strikethrough size={14} />
          </ToolbarBtn>
          <ToolbarBtn onClick={() => editor?.chain().focus().toggleUnderline().run()} active={editor?.isActive('underline')} title="Underline">
            <UnderlineIcon size={14} />
          </ToolbarBtn>
          <div className="inline-flex" ref={colorAnchorRef} data-popover-trigger>
            <ToolbarBtn
              onClick={() => setColorPopover((m) => (m === 'color' ? null : 'color'))}
              active={!!editor?.getAttributes('textStyle')?.color || colorPopover === 'color'}
              title="Text colour"
            >
              <Palette size={14} />
            </ToolbarBtn>
          </div>
          <div className="inline-flex" ref={highlightAnchorRef} data-popover-trigger>
            <ToolbarBtn
              onClick={() => setColorPopover((m) => (m === 'highlight' ? null : 'highlight'))}
              active={editor?.isActive('highlight') || colorPopover === 'highlight'}
              title="Highlight"
            >
              <Highlighter size={14} />
            </ToolbarBtn>
          </div>
          <span className="mx-1 h-5 w-px bg-ink-700" />
          <ToolbarBtn
            onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()}
            active={editor?.isActive('heading', { level: 1 })}
          >
            <Heading1 size={14} />
          </ToolbarBtn>
          <ToolbarBtn
            onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
            active={editor?.isActive('heading', { level: 2 })}
          >
            <Heading2 size={14} />
          </ToolbarBtn>
          <ToolbarBtn
            onClick={() => editor?.chain().focus().toggleBulletList().run()}
            active={editor?.isActive('bulletList')}
          >
            <List size={14} />
          </ToolbarBtn>
          <ToolbarBtn
            onClick={() => editor?.chain().focus().toggleOrderedList().run()}
            active={editor?.isActive('orderedList')}
          >
            <ListOrdered size={14} />
          </ToolbarBtn>
          <ToolbarBtn
            onClick={() => editor?.chain().focus().toggleCodeBlock().run()}
            active={editor?.isActive('codeBlock')}
          >
            <Code size={14} />
          </ToolbarBtn>
          <ToolbarBtn
            onClick={() => editor?.chain().focus().toggleBlockquote().run()}
            active={editor?.isActive('blockquote')}
          >
            <Quote size={14} />
          </ToolbarBtn>
          <span className="mx-1 h-5 w-px bg-ink-700" />
          <ToolbarBtn
            onClick={() => (editor?.chain().focus() as any).toggleSuperscript().run()}
            active={editor?.isActive('superscript')}
            title="Superscript"
          >
            <SuperscriptIcon size={14} />
          </ToolbarBtn>
          <ToolbarBtn
            onClick={() => (editor?.chain().focus() as any).toggleSubscript().run()}
            active={editor?.isActive('subscript')}
            title="Subscript"
          >
            <SubscriptIcon size={14} />
          </ToolbarBtn>
          <ToolbarBtn
            onClick={cycleTextAlign}
            active={
              editor?.isActive({ textAlign: 'left' }) ||
              editor?.isActive({ textAlign: 'center' }) ||
              editor?.isActive({ textAlign: 'right' }) ||
              editor?.isActive({ textAlign: 'justify' })
            }
            title="Cycle text alignment (none → left → center → right → justify)"
          >
            {editor?.isActive({ textAlign: 'center' }) ? (
              <AlignCenter size={14} />
            ) : editor?.isActive({ textAlign: 'right' }) ? (
              <AlignRight size={14} />
            ) : editor?.isActive({ textAlign: 'justify' }) ? (
              <AlignJustify size={14} />
            ) : (
              <AlignLeft size={14} />
            )}
          </ToolbarBtn>
          <span className="mx-1 h-5 w-px bg-ink-700" />
          <div className="inline-flex" ref={tableAnchorRef} data-popover-trigger>
            <ToolbarBtn
              onClick={() => setTableMenuOpen((v) => !v)}
              active={editor?.isActive('table') || tableMenuOpen}
              title="Table"
            >
              <TableIcon size={14} />
            </ToolbarBtn>
          </div>
          <ToolbarBtn onClick={openPasswordInsert} title="Insert password field">
            <KeyRound size={14} />
          </ToolbarBtn>
        </div>
      )}

      {/* Popovers — rendered as toolbar siblings (not children) so the
          toolbar's `overflow-x-auto` doesn't clip them. position:fixed inside
          the popover anchors against the trigger's bounding rect. They stay
          inside this <section> so EditorPane's auto-Done click-out listener
          treats clicks on swatches/menu items as "inside the editor". */}
      {editor && colorPopover === 'color' && colorAnchorRef.current && (
        <ColorPopover
          editor={editor}
          mode="color"
          onClose={closeColorPopover}
          anchor={colorAnchorRef.current.getBoundingClientRect()}
        />
      )}
      {editor && colorPopover === 'highlight' && highlightAnchorRef.current && (
        <ColorPopover
          editor={editor}
          mode="highlight"
          onClose={closeColorPopover}
          anchor={highlightAnchorRef.current.getBoundingClientRect()}
        />
      )}
      {editor && tableMenuOpen && tableAnchorRef.current && (
        <TableMenu
          editor={editor}
          onClose={closeTableMenu}
          anchor={tableAnchorRef.current.getBoundingClientRect()}
        />
      )}
      {editor && canEdit && <TableBubbleMenu editor={editor} />}
      {editor && canEdit && <TableChrome editor={editor} />}

      <div
        className={`relative flex-1 overflow-y-auto p-3 md:p-6 min-h-0 ${
          dropOver ? 'bg-dragon-500/5' : ''
        }`}
        onDragOver={(e) => {
          if (!activeDocId || !doc || !canEdit) return;
          const accept =
            e.dataTransfer.types.includes(ATTACHMENT_DRAG_MIME) || dragHasFiles(e);
          if (!accept) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = 'copy';
          setDropOver(true);
        }}
        onDragLeave={(e) => {
          if (e.currentTarget.contains(e.relatedTarget as Node)) return;
          setDropOver(false);
        }}
        onDrop={async (e) => {
          e.preventDefault();
          setDropOver(false);
          if (!activeDocId || !doc || !vaultKey || !canEdit) return;
          setDropError(null);

          let payloads: AttachmentDragPayload[] = [];
          const inApp = readAttachmentDrag(e);
          if (inApp) {
            payloads = [inApp];
          } else if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            setDropBusy(true);
            try {
              payloads = await uploadFiles(
                e.dataTransfer.files,
                doc.customerId,
                vaultKey,
              );
            } catch (err: any) {
              setDropError(err?.body?.error ?? err?.message ?? 'Upload failed.');
              setTimeout(() => setDropError(null), 4000);
              setDropBusy(false);
              return;
            }
            setDropBusy(false);
          }
          if (payloads.length === 0) return;

          // Insert into the live editor — the Y.Doc change replicates to
          // other viewers and a snapshot follows on debounce.
          if (editor) {
            for (const p of payloads) {
              if (p.category === 'image') {
                (editor.chain().focus() as any)
                  .insertInlineImage({ attachmentId: p.id, alt: p.filename })
                  .run();
              } else {
                (editor.chain().focus() as any)
                  .insertAttachmentChip({
                    attachmentId: p.id,
                    filename: p.filename,
                    mimetype: p.mimetype,
                  })
                  .run();
              }
            }
          }
        }}
      >
        <EditorContent editor={editor} />
        {dropOver && (
          <div className="pointer-events-none absolute inset-2 rounded-lg border-2 border-dashed border-dragon-500/70 flex items-center justify-center bg-dragon-500/5 text-dragon-500 text-sm font-medium">
            Drop to attach to this {activeTabId ? 'tab' : 'page'}
          </div>
        )}
        {dropBusy && (
          <div className="pointer-events-none absolute bottom-4 right-4 text-xs text-ink-400 bg-ink-900/80 px-2 py-1 rounded">
            Uploading…
          </div>
        )}
        {dropError && (
          <div className="absolute top-2 right-2 text-xs text-red-300 bg-red-950/80 border border-red-900 px-2 py-1 rounded">
            {dropError}
          </div>
        )}
      </div>

      {reveal && (
        <RevealPasswordModal
          id={reveal.id}
          label={reveal.label}
          value={reveal.value}
          onClose={() => setReveal(null)}
        />
      )}
      {pwEdit && (
        <PasswordEditorModal
          mode={pwEdit.mode}
          initialLabel={pwEdit.mode === 'edit' ? pwEdit.label : ''}
          initialValue={pwEdit.mode === 'edit' ? pwEdit.value : ''}
          externalError={pwError}
          onSave={savePasswordField}
          onClose={() => {
            setPwEdit(null);
            setPwError(null);
          }}
        />
      )}
      {showHistory && activeDocId && (
        <VersionHistoryDrawer
          documentId={activeDocId}
          onClose={() => setShowHistory(false)}
        />
      )}
    </section>
  );
}

function ToolbarBtn({
  children,
  active,
  ...rest
}: {
  children: React.ReactNode;
  active?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...rest}
      className={`btn-ghost !px-2 !py-1 ${active ? 'bg-ink-800 text-white' : ''}`}
    >
      {children}
    </button>
  );
}
